import React, { FC } from "react";
import { UserInput } from "../../validations/userCreateValidation";
import { Control, FieldErrors, SubmitHandler } from "react-hook-form";
import { CreateUserBasicInfo } from "../createUser/CreateUserBasicInfo";
import { pick } from "../../utils/pick";
import { CreateUserRoleAndGroup } from "../createUser/CreateUserRoleAndGroup";
import { CreateUserAdditionalAttributes } from "../createUser/CreateUserAdditionalAttributes";
import Stack from "@carrier-io/air-react/Stack";
import { Button } from "../button/Button";
import Box from "@carrier-io/air-react/Box";
import { UpdateProfileInformation } from "../updateProfile/UpdateProfileInformation";
import { DeactivateUser } from "../editUser/DeactivateUser";
import { UserStatusT } from "../../constants/user";

interface UserFormProps {
  control: Control<UserInput>;
  errors: FieldErrors<UserInput>;
  onSubmit: SubmitHandler<UserInput>;
  loading: boolean;
  isValid?: boolean;
  onCancel?: () => void;
  loadingGetData?: boolean;
  submitButtonText?: string;
  showUpdateUserInfo?: boolean;
  showUserBasicInfo?: boolean;
  showAuthMethod?: boolean;
  showUserRoleAndGroup?: boolean;
  showUserAdditionalAttributes?: boolean;
  showDeactivateUser?: boolean;
  emailDisabled?: boolean;
  existingUserStatus?: UserStatusT;
  refetchExistingUser?: () => void;
}

export const UserForm: FC<UserFormProps> = ({
  control,
  errors,
  onSubmit,
  loading,
  isValid,
  onCancel,
  submitButtonText = "Send Invitation",
  showUserBasicInfo = true,
  showAuthMethod = true,
  showUserRoleAndGroup = true,
  showUserAdditionalAttributes = true,
  showUpdateUserInfo = false,
  showDeactivateUser = false,
  emailDisabled = false,
  loadingGetData = false,
  existingUserStatus,
  refetchExistingUser,
}) => {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(control._defaultValues as UserInput);
      }}
    >
      {showUpdateUserInfo && (
        <UpdateProfileInformation
          control={control}
          errors={pick(errors, ["firstName", "lastName"])}
        />
      )}
      {showUserBasicInfo && (
        <CreateUserBasicInfo
          control={control}
          errors={pick(errors, [
            "email",
            "firstName",
            "lastName",
            "authMethod",
          ])}
          showAuthMethod={showAuthMethod}
          emailDisabled={emailDisabled}
          isLoading={loadingGetData}
        />
      )}
      {showUserRoleAndGroup && (
        <CreateUserRoleAndGroup
          control={control}
          errors={pick(errors, ["groups", "roles"])}
        />
      )}
      {showUserAdditionalAttributes && (
        <CreateUserAdditionalAttributes
          control={control}
          errors={pick(errors, ["attributes"])}
          isLoading={loadingGetData}
        />
      )}
      {showDeactivateUser && (
        <DeactivateUser
          isLoading={loadingGetData}
          existingUserStatus={existingUserStatus}
          {...(refetchExistingUser && { refetchExistingUser })}
        />
      )}

      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Button color="primary" variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Box sx={{ width: "128px" }}>
          <Button
            fullWidth
            isLoading={loading}
            disabled={!isValid || loading}
            color="primary"
            variant="contained"
            type="submit"
          >
            {submitButtonText}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
